import { useSessionUser } from '@contexts/SessionUserContext';
import { useEffect, useRef, useState } from 'react';

import { logger } from './logger';

export const useFocus: () => [
  React.RefObject<HTMLElement>,
  () => void,
] = () => {
  const htmlElRef = useRef<HTMLElement>(null);
  const setFocus = () =>
    setTimeout(() => {
      if (htmlElRef.current) {
        htmlElRef.current.focus();
      }
    }, 0);

  return [htmlElRef, setFocus] as [React.RefObject<HTMLElement>, () => void];
};

export function usePersistentState<T>(
  key: string,
  initialValue: T,
): [T, (value: T) => void] {
  const scopedKey = `persistentState-${key}`;

  const getValueFromLocalStorage = () => {
    const value = localStorage.getItem(scopedKey);
    if (!value) return initialValue;
    try {
      return JSON.parse(value);
    } catch {
      logger.error(
        {
          userScopedKey: scopedKey,
          value,
        },
        'Failed to parse persisted state value from localStorage',
      );
      return initialValue;
    }
  };

  const [state, setInternalState] = useState<T>(getValueFromLocalStorage());

  useEffect(() => {
    const value = localStorage.getItem(scopedKey);

    if (!value) return;

    try {
      setInternalState(JSON.parse(value));
    } catch {
      logger.error(
        {
          userScopedKey: scopedKey,
          value,
        },
        'Failed to parse persisted state value from localStorage',
      );
      setInternalState(initialValue);
    }
  }, [initialValue, scopedKey]);

  const setState = (value: T) => {
    localStorage.setItem(scopedKey, JSON.stringify(value));
    setInternalState(value);
  };

  return [state, setState];
}

export const useSessionUserId = () => {
  // Use the SessionUserContext instead of maintaining separate state
  const { userId, updateUserId } = useSessionUser();

  return {
    userId,
    updateUserId,
  };
};
